import { useState, useEffect } from "react";
import { Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  fetchSubscriptionPreferences,
  updateSubscriptionPreferences,
  type SubscriptionPreferenceItem,
  type SubscriptionPreferenceUpdateItem,
} from "@/services/api";
import toast from "react-hot-toast";

const CHANNEL_EMAIL = "EMAIL";
const TYPE_DIGEST = "DIGEST";
const TYPE_REMINDERS = "REMINDERS";

interface SettingsContentProps {
  userInfo: { id: string; email: string; name: string };
  premiumUntil: string | null;
  reload: boolean;
}

function getEmailPrefs(items: SubscriptionPreferenceItem[]) {
  const digestItem = items.find((i) => i.channel === CHANNEL_EMAIL && i.type === TYPE_DIGEST);
  const remindersItem = items.find((i) => i.channel === CHANNEL_EMAIL && i.type === TYPE_REMINDERS);
  return {
    digest: digestItem?.is_active ?? true,
    reminders: remindersItem?.is_active ?? true,
  };
}

export function SettingsContent({ userInfo, reload }: SettingsContentProps) {
  const [prefs, setPrefs] = useState<SubscriptionPreferenceItem[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Local form state: email master on/off, then digest + reminders toggles
  const [isActive, setIsActive] = useState(true);
  const [digest, setDigest] = useState(true);
  const [reminders, setReminders] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const data = await fetchSubscriptionPreferences();
        setPrefs(data);
        const { digest: d, reminders: r } = getEmailPrefs(data);
        setDigest(d);
        setReminders(r);
        setIsActive(d || r);
      } catch (e) {
        console.error("Failed to load communication preferences:", e);
        toast.error("Failed to load preferences");
        setPrefs([]);
        setIsActive(true);
        setDigest(true);
        setReminders(true);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [reload]);

  const handleSave = async () => {
    const prev = prefs ? getEmailPrefs(prefs) : { digest: true, reminders: true };
    const digestActive = isActive && digest;
    const remindersActive = isActive && reminders;

    const payload: SubscriptionPreferenceUpdateItem[] = [];
    if (digestActive !== prev.digest) {
      payload.push({ channel: CHANNEL_EMAIL, type: TYPE_DIGEST, is_active: digestActive });
    }
    if (remindersActive !== prev.reminders) {
      payload.push({ channel: CHANNEL_EMAIL, type: TYPE_REMINDERS, is_active: remindersActive });
    }

    if (payload.length === 0) return;

    try {
      setSaving(true);
      await updateSubscriptionPreferences(payload);
      const data = await fetchSubscriptionPreferences();
      setPrefs(data);
      toast.success("Communication preferences updated");
    } catch (e) {
      console.error("Failed to update preferences:", e);
      toast.error("Failed to update preferences");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold text-white mb-2">Settings</h2>
          <p className="text-gray-400">Manage your communication preferences.</p>
        </div>
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500" />
        </div>
      </div>
    );
  }

  const { digest: prevDigest, reminders: prevReminders } = prefs ? getEmailPrefs(prefs) : { digest: true, reminders: true };
  const hasChanges =
    !prefs ||
    (isActive && digest) !== prevDigest ||
    (isActive && reminders) !== prevReminders;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-white mb-2">Communication Preferences</h2>
        <p className="text-gray-400">Manage your account and communication preferences.</p>
      </div>

      <Card className="bg-slate-800/50 border-slate-700 max-w-2xl">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Mail className="h-5 w-5 text-purple-400" />
            Email Notifications
          </CardTitle>
          <CardDescription className="text-gray-400">
          Receive emails at {userInfo.email}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">

          {/* Types when email is on */}
          {isActive && (
            <div className="space-y-4 pl-1">
              <div className="space-y-3">
                <label className="flex items-start gap-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={digest}
                    onChange={(e) => setDigest(e.target.checked)}
                    className="h-4 w-4 mt-1.5 rounded border-slate-600 bg-slate-800 text-purple-600 focus:ring-purple-500 focus:ring-offset-slate-900"
                  />
                  <div>
                    <span className="text-white group-hover:text-gray-200">Weekly Email Digest</span>
                    <p className="text-sm text-gray-400">Periodic Insights and Updates based on your saved content</p>
                  </div>
                </label>
                <label className="flex items-start gap-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={reminders}
                    onChange={(e) => setReminders(e.target.checked)}
                    className="h-4 w-4 mt-1.5 rounded border-slate-600 bg-slate-800 text-purple-600 focus:ring-purple-500 focus:ring-offset-slate-900"
                  />
                  <div>
                    <span className="text-white group-hover:text-gray-200">Reminders</span>
                    <p className="text-sm text-gray-400">Reminders and notifications emails</p>
                  </div>
                </label>
              </div>
            </div>
          )}

          <Button
            onClick={handleSave}
            disabled={saving || !hasChanges}
            className="bg-purple-600 hover:bg-purple-700 text-white"
          >
            {saving ? "Saving…" : "Save preferences"}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
